import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { OpenTaskBanner } from '@/components/px/OpenTaskBanner'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { resourcesNav } from '@/components/px/nav'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  COHORTS_QUEUED,
  COHORTS_SECTION,
  FUNNEL,
  FUNNEL_SECTION,
  HUB_BADGE,
  KPIS,
  OPEN_ROLES_BANNER,
  PIPELINE_CHROME,
  PIPELINE_COPY,
  PLAN,
  PLAN_SECTION,
  RUNNING_COHORTS,
  type CohortRow,
  type FunnelStage,
  type PlanYear,
  type Segment,
} from '@/demo/content/talentPipeline'
import { cn } from '@/lib/utils'

const HEAD_CLASS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

/** Prose in which some words are real destinations (never a dead phrase). */
function Prose({ segments, className }: { segments: readonly Segment[]; className?: string }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.kind === 'text' ? (
          <span key={index}>{segment.text}</span>
        ) : (
          <Link
            key={index}
            to={segment.href}
            className={cn('font-medium underline underline-offset-2 hover:no-underline', className)}
          >
            {segment.label}
          </Link>
        )
      )}
    </>
  )
}

/**
 * The two Competence Center stages carry the people accent; inks are darkened
 * against the lavender wash so every figure and note stays AA.
 */
const STAGE_SKIN: Record<FunnelStage['tone'], { box: string; label: string; note: string }> = {
  plain: { box: 'border-border bg-card', label: 'text-muted-foreground', note: 'text-muted-foreground' },
  growth: { box: 'border-people-bg bg-people-bg', label: 'text-people-ink', note: 'text-ink-soft' },
}

function FunnelChip({ stage }: { stage: FunnelStage }) {
  const skin = STAGE_SKIN[stage.tone]
  const body = (
    <>
      <div className={cn('text-[11px] font-semibold uppercase tracking-[0.08em]', skin.label)}>
        {stage.label}
      </div>
      <div className="v-figure mt-0.5 font-serif text-[26px] font-semibold leading-8 tabular-nums text-foreground">
        {stage.count}
      </div>
      <div className={cn('mt-0.5 text-xs', skin.note)}>{stage.note}</div>
    </>
  )

  return (
    <div className={cn('min-w-[9.5rem] flex-1 rounded-lg border px-3.5 py-3', skin.box)}>
      {stage.href === undefined ? (
        body
      ) : (
        <Link
          to={stage.href}
          title={FUNNEL_SECTION.stageLinkTitle}
          className="block underline-offset-4 hover:underline"
        >
          {body}
        </Link>
      )}
    </div>
  )
}

function CohortTableRow({ cohort }: { cohort: CohortRow }) {
  const { trainees = [] } = cohort

  return (
    <TableRow>
      <TableHead scope="row" className="h-auto max-w-[22rem] whitespace-normal py-2.5 align-top">
        <span className="block text-sm text-foreground">{cohort.label}</span>
        {trainees.length > 0 && (
          <span className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 text-xs font-normal text-muted-foreground">
            <span>{COHORTS_SECTION.inclusionPrefix}</span>
            {trainees.map((trainee, index) => (
              <span key={trainee.id} className="inline-flex items-baseline gap-1.5">
                {index > 0 && <span aria-hidden>·</span>}
                <Link
                  to={trainee.href}
                  className="text-ink-soft underline underline-offset-2 hover:text-foreground"
                >
                  {trainee.name}
                </Link>
              </span>
            ))}
          </span>
        )}
      </TableHead>

      <TableCell className="v-figure py-2.5 align-top text-right text-sm tabular-nums text-foreground">
        {cohort.peopleLabel}
      </TableCell>

      <TableCell className="py-2.5 align-top">
        <span className="v-figure block text-sm tabular-nums text-foreground">
          {cohort.progressLabel}
        </span>
        <Progress
          value={cohort.progressPct}
          aria-hidden
          className="mt-1.5 h-1 w-28 bg-hairline"
        />
      </TableCell>

      <TableCell className="v-figure py-2.5 align-top text-right text-sm tabular-nums text-foreground">
        {cohort.billableWeekLabel}
        <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
          {cohort.startWeekLabel}
        </span>
      </TableCell>

      <TableCell className="py-2.5 align-top">
        <StatusPill tone="people">{COHORTS_SECTION.runningTag}</StatusPill>
      </TableCell>
    </TableRow>
  )
}

function PlanTableRow({ row }: { row: PlanYear }) {
  return (
    <TableRow>
      <TableHead scope="row" className="h-auto py-2.5 align-top">
        <span className="v-figure block text-sm tabular-nums text-foreground">{row.year}</span>
        {row.note !== null && (
          <span className="mt-0.5 block text-xs font-normal text-muted-foreground">{row.note}</span>
        )}
      </TableHead>

      <TableCell className="v-figure py-2.5 align-top text-right text-sm tabular-nums text-foreground">
        {row.fteLabel}
      </TableCell>

      <TableCell className="v-figure py-2.5 align-top text-right text-sm tabular-nums text-muted-foreground">
        {row.changeLabel}
      </TableCell>

      <TableCell className="py-2.5 align-top text-right">
        <span className="v-figure block text-sm tabular-nums text-foreground">
          {row.billableLabel}
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {PLAN_SECTION.billableCellLabel(row)}
        </span>
      </TableCell>
    </TableRow>
  )
}

/**
 * 1.7 Talent pipeline — the funnel, the cohorts running and the headcount plan
 * for the Competence Center. Counts only: the page carries no candidate rows,
 * and each plan year is gated on the capacity forecast rather than promised.
 */
export default function TalentPipelinePage() {
  return (
    <AppShell
      moduleLabel={PIPELINE_CHROME.moduleLabel}
      breadcrumb={PIPELINE_CHROME.breadcrumb}
      contextPill={PIPELINE_CHROME.contextPill}
      nav={resourcesNav}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="font-serif text-2xl font-semibold">{PIPELINE_COPY.title}</h1>
          <StatusPill tone="people">{HUB_BADGE}</StatusPill>
          {/* PX-W6.2 (AC-8): the demo badge — seed content, network-quiet. */}
          <ConceptBadge />
        </div>
        <p className="text-sm text-muted-foreground">{PIPELINE_COPY.lede}</p>
      </header>

      <section className="mt-5" aria-labelledby="funnel-label">
        <SectionLabel className="pb-2">
          <span id="funnel-label">{FUNNEL_SECTION.label}</span>
        </SectionLabel>
        <ol className="flex flex-wrap items-stretch gap-2">
          {FUNNEL.map((stage, index) => (
            <li key={stage.key} className="flex flex-1 items-center gap-2">
              {index > 0 && (
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              )}
              <FunnelChip stage={stage} />
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-5" aria-labelledby="cohorts-label">
        <SectionLabel className="pb-1">
          <span id="cohorts-label">{COHORTS_SECTION.label}</span>
        </SectionLabel>
        <p className="max-w-3xl pb-2 text-sm text-muted-foreground">{COHORTS_SECTION.caption}</p>
        <div className="rounded-lg border border-border bg-card">
          <Table>
            <TableCaption className="sr-only">{COHORTS_SECTION.caption}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className={cn(HEAD_CLASS, 'pl-3')}>
                  {COHORTS_SECTION.columns.cohort}
                </TableHead>
                <TableHead scope="col" className={cn(HEAD_CLASS, 'text-right')}>
                  {COHORTS_SECTION.columns.people}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {COHORTS_SECTION.columns.progress}
                </TableHead>
                <TableHead scope="col" className={cn(HEAD_CLASS, 'text-right')}>
                  {COHORTS_SECTION.columns.billable}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  <span className="sr-only">{COHORTS_SECTION.runningTag}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {RUNNING_COHORTS.map((cohort) => (
                <CohortTableRow key={cohort.key} cohort={cohort} />
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-soft">
          <Prose segments={COHORTS_QUEUED} className="text-accent-foreground" />
        </p>
      </section>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {KPIS.map((kpi) => (
          <KpiCard
            key={kpi.key}
            label={kpi.label}
            figure={kpi.figure}
            qualifier={<Prose segments={kpi.qualifier} className="text-accent-foreground" />}
          />
        ))}
      </div>

      <section className="mt-5" aria-labelledby="plan-label">
        <SectionLabel className="pb-1">
          <span id="plan-label">{PLAN_SECTION.label}</span>
        </SectionLabel>
        <p className="max-w-3xl pb-2 text-sm text-muted-foreground">{PLAN_SECTION.caption}</p>
        <div className="rounded-lg border border-border bg-card">
          <Table>
            <TableCaption className="sr-only">{PLAN_SECTION.caption}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className={cn(HEAD_CLASS, 'pl-3')}>
                  {PLAN_SECTION.columns.year}
                </TableHead>
                <TableHead scope="col" className={cn(HEAD_CLASS, 'text-right')}>
                  {PLAN_SECTION.columns.fte}
                </TableHead>
                <TableHead scope="col" className={cn(HEAD_CLASS, 'text-right')}>
                  {PLAN_SECTION.columns.change}
                </TableHead>
                <TableHead scope="col" className={cn(HEAD_CLASS, 'text-right')}>
                  {PLAN_SECTION.columns.billable}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {PLAN.map((row) => (
                <PlanTableRow key={row.key} row={row} />
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground">
          <Prose segments={PLAN_SECTION.gating} className="text-accent-foreground" />
        </p>
      </section>

      <div className="mt-5">
        <OpenRolesBanner />
      </div>

      <DensityNote>
        <Prose segments={PIPELINE_COPY.densityNote} />
      </DensityNote>
    </AppShell>
  )
}

/** The roles the skills matrix opened — dark furniture, one way back to it. */
function OpenRolesBanner() {
  return (
    <OpenTaskBanner
      eyebrow={OPEN_ROLES_BANNER.eyebrow}
      title={OPEN_ROLES_BANNER.title}
      meta={OPEN_ROLES_BANNER.meta}
    >
      <Link
        to={OPEN_ROLES_BANNER.link.href}
        className="mt-3 inline-block text-sm font-medium text-primary-foreground underline underline-offset-4 hover:no-underline"
      >
        {OPEN_ROLES_BANNER.link.label}
      </Link>
    </OpenTaskBanner>
  )
}
